<template>
  <transition-group :name="transitionName">
    <div
      v-for="(question, index) in questions"
      :key="keyName + '-' + index + question.id"
      :class="landscape ? 'bb-1 bc-outlined-grey' : ''"
      data-testid="question-set"
    >
      <div v-if="shouldShowQuestion(index)">
        <template v-if="question.is_section">
          <v-card
            :class="sectionClass(index)"
            :flat="flat"
            :variant="outlinedSections ? 'outlined' : 'elevated'"
            tile
          >
            <v-card-text>
              <div
                v-if="question.title"
                aria-level="2"
                class="fw-500 fs-16"
                role="heading"
              >
                {{ $bct(question.title) }}
              </div>

              <div
                v-if="question.subtitle"
                aria-level="3"
                class="fw-500 fs-14"
                role="heading"
              >
                {{ $bct(question.subtitle) }}
              </div>
            </v-card-text>
          </v-card>
        </template>

        <template v-else>
          <QuestionWithField
            v-if="question.format === 'text'"
            v-model="modelValue"
            @back="emit('back', index)"
            @change="handleChange($event)"
            @change:attachments="emit('change:attachments')"
            @change:input="emit('change:input', $event)"
            @click:notes="emit('click:notes', question)"
            @next="emit('next', index)"
            :attachment-delete-disabled="attachmentDeleteDisabled || readonly"
            :attachment-group-id="attachmentGroupId"
            :attachment-owner-id="attachmentOwnerId"
            :attachment-owner-type="attachmentOwnerType"
            :attachment-status="attachmentStatus"
            :attachment-tags-supplements="attachmentTagsSupplements"
            :attachment-upload-disabled="attachmentUploadDisabled"
            :attachments="attachments"
            :autofocus="autofocus"
            :border="border"
            :color="color"
            :condensed="condensed"
            :dense="dense"
            :display-answer="displayAnswer"
            :display-notes="displayNotes"
            :elevation="elevation"
            :enable-screen-reader-hint="mode === 'review'"
            :expanded="expanded"
            :header="header"
            :hide-actions="hideActions"
            :hide-field="hideField"
            :inline-verification="inlineVerification"
            :landscape="landscape"
            :notes-count="noteCount(question)"
            :outlined="outlined"
            :paddingless="paddingless"
            :processing="processing"
            :question="question"
            :readonly="readonly"
            :reversible="reversible"
            :schema="schema"
            :show-indicators="showIndicators"
            :show-passing="showPassing"
            :tile="tile"
            :very-dense="veryDense"
          />

          <AddressQuestion
            v-if="question.format === 'address'"
            v-model="modelValue"
            @back="emit('back', index)"
            @change="emit('change', $event)"
            @next="emit('next', index)"
            :border="border"
            :color="color"
            :condensed="condensed"
            :dense="dense"
            :elevation="elevation"
            :expanded="expanded"
            :header="header"
            :hide-actions="hideActions"
            :hide-field="hideField"
            :landscape="landscape"
            :notes-count="noteCount(question)"
            :outlined="outlined"
            :paddingless="paddingless"
            :processing="processing"
            :question="question"
            :readonly="readonly"
            :reversible="reversible"
            :schema="schema"
            :show-indicators="showIndicators"
            :show-passing="showPassing"
            :tile="tile"
            :very-dense="veryDense"
          />

          <DocumentQuestion
            v-if="question.format === 'document'"
            v-model="modelValue"
            @back="emit('back', index)"
            @change="emit('change', $event)"
            @change:attachments="emit('change:attachments')"
            @change:input="emit('change:input', $event)"
            @click:notes="emit('click:notes', question)"
            @next="emit('next', index)"
            :attachment-delete-disabled="attachmentDeleteDisabled || readonly"
            :attachment-group-id="attachmentGroupId"
            :attachment-owner-id="attachmentOwnerId"
            :attachment-owner-type="attachmentOwnerType"
            :attachment-status="attachmentStatus"
            :attachment-tags-supplements="attachmentTagsSupplements"
            :attachment-upload-disabled="attachmentUploadDisabled"
            :attachments="attachments"
            :autofocus="autofocus"
            :border="border"
            :color="color"
            :condensed="condensed"
            :dense="dense"
            :display-notes="displayNotes"
            :elevation="elevation"
            :expanded="expanded"
            :header="header"
            :hide-actions="hideActions"
            :landscape="landscape"
            :notes-count="noteCount(question)"
            :outlined="outlined"
            :paddingless="paddingless"
            :processing="processing"
            :question="question"
            :reversible="reversible"
            :schema="schema"
            :show-indicators="showIndicators"
            :tile="tile"
            :very-dense="veryDense"
          />

          <ProviderQuestion
            v-if="question.format === 'provider'"
            v-model="modelValue"
            @back="emit('back', index)"
            @change="emit('change', $event)"
            @change:input="emit('change:input', $event)"
            @click:notes="emit('click:notes', question)"
            @next="emit('next', index)"
            :border="border"
            :color="color"
            :condensed="condensed"
            :dense="dense"
            :elevation="elevation"
            :expanded="expanded"
            :header="header"
            :hide-actions="hideActions"
            :landscape="landscape"
            :notes-count="noteCount(question)"
            :outlined="outlined"
            :paddingless="paddingless"
            :processing="processing"
            :question="question"
            :readonly="readonly"
            :reversible="reversible"
            :show-indicators="showIndicators"
            :show-provider-change-notice="showProviderChangeNotice"
            :tile="tile"
            :very-dense="veryDense"
          />

          <IncomeQuestion
            v-if="question.format === 'income'"
            v-model="modelValue"
            @back="emit('back', index)"
            @change="emit('change', $event)"
            @next="emit('next', index)"
            :color="color"
            :dense="dense"
            :elevation="elevation"
            :header="header"
            :hide-actions="hideActions"
            :hide-field="hideField || question.conditions_failed"
            :landscape="landscape"
            :outlined="outlined"
            :paddingless="paddingless"
            :processing="processing"
            :question="question"
            :readonly="readonly"
            :reversible="reversible"
            :show-indicators="showIndicators"
            :tile="tile"
            :very-dense="veryDense"
          />

          <ChangeResponseButton
            v-if="mode == 'review'"
            @jump="emit('jump', $event)"
            :section="`${keyName}-${index}`"
          />

          <v-divider
            v-if="divided"
            :class="dividedClass"
          />
        </template>
      </div>
    </div>
  </transition-group>
</template>

<script setup>
import AddressQuestion from '@/shared/components/form/AddressQuestion.vue';
import ChangeResponseButton from '@/shared/components/form/ChangeResponseButton.vue';
import DocumentQuestion from '@/shared/components/form/DocumentQuestion.vue';
import IncomeQuestion from '@/shared/components/form/IncomeQuestion.vue';
import ProviderQuestion from '@/shared/components/form/ProviderQuestion.vue';
import QuestionWithField from '@/shared/components/form/QuestionWithField.vue';

const $bct = inject('translator');

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits([
  'back',
  'change:attachments',
  'change:input',
  'change',
  'click:notes',
  'jump',
  'next',
]);

/**
 * @property {boolean} expanded - Display all questions (e.g. reviewing a Subsidy application as a parent before submit)
 * @property {string} section - Display a specific section of questions (e.g. navigating through questions to complete a Subsidy application)
 */

const props = defineProps({
  answerValidationComplete: {
    type: Boolean,
    default: true,
  },
  attachmentGroupId: {
    type: String,
    default: null,
  },
  attachmentOwnerId: {
    type: String,
    default: null,
  },
  attachmentOwnerType: {
    type: String,
    default: null,
  },
  attachmentDeleteDisabled: {
    type: [Boolean, null],
    default: null,
  },
  attachmentTagsSupplements: {
    type: Array,
    default: () => [],
  },
  attachmentUploadDisabled: {
    type: [Boolean, null],
    default: null,
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  attachmentStatus: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  border: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: 'transparent',
  },
  condensed: {
    type: Boolean,
    default: false,
  },
  dense: {
    type: Boolean,
    default: false,
  },
  displayAnswer: {
    type: Boolean,
    default: false,
  },
  displayNotes: {
    type: Boolean,
    default: false,
  },
  divided: {
    type: Boolean,
    default: false,
  },
  elevation: {
    default: 0,
    type: Number,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  flat: {
    type: Boolean,
    default: false,
  },
  header: {
    type: String,
    default: null,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  hideField: {
    type: Boolean,
    default: false,
  },
  inlineVerification: {
    type: Boolean,
    default: false,
  },
  keyName: {
    type: String,
    default: null,
  },
  landscape: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'edit',
  },
  notes: {
    type: Array,
    default: () => [],
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  outlinedSections: {
    type: Boolean,
    default: false,
  },
  paddingless: {
    type: Boolean,
    default: false,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  questions: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  reversible: {
    type: Boolean,
    default: true,
  },
  schema: {
    type: Object,
    default: null,
  },
  section: {
    type: String,
    default: null,
  },
  showProviderChangeNotice: {
    type: Boolean,
    default: false,
  },
  showIndicators: {
    type: Boolean,
    default: false,
  },
  showPassing: {
    type: Boolean,
    default: false,
  },
  tile: {
    type: Boolean,
    default: false,
  },
  transitionName: {
    type: String,
    default: null,
  },
  veryDense: {
    type: Boolean,
    default: false,
  },
});

const modelValue = defineModel({ type: Object });

const dividedClass = computed(() => {
  if (props.landscape) {
    return 'my-0';
  }
  if (props.dense) {
    return 'mb-4';
  }
  if (props.veryDense) {
    return 'mb-2';
  }
  return 'mb-6';
});

function handleChange(event) {
  emit('change', event);
}

function noteCount(question) {
  return (props.notes || []).filter((note) => note.question_id === question.id).length;
}

const shouldShowQuestion = computed(() => {
  return (index) => {
    // Expanded mode: always show all questions (even during processing/saving)
    if (props.expanded) return true;

    // Step-by-step mode: don't show anything while processing
    if (props.processing) return false;

    // Step-by-step mode: require validation complete and current section
    return (
      props.answerValidationComplete &&
      props.section &&
      props.section === `${props.keyName}-${index}`
    );
  };
});

function sectionClass(index) {
  const classes = [];
  if (props.landscape) {
    classes.push('mt-0');
    classes.push('bg-very-light-grey');
  } else {
    classes.push(index > 0 ? 'mt-8 mb-6' : 'mt-0 mb-6');
    classes.push('bg-super-light-grey');
  }
  return classes.join(' ');
}
</script>
